import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useCommandSearchContext } from '../../context/CommandSearchContext';
import { useQuickAdd } from '../../context/QuickAddServiceContext';

interface SearchResult {
  id: string;
  title: string;
  description?: string;
  path: string;
  icon: React.ReactNode;
  category: string;
  keywords: string[];
  requiredPermission?: string;
  /** Si existe, se ejecuta en vez de navegar a `path` */
  action?: () => void;
}

const MobileCommandSearch: React.FC = () => {
  const { isSearchOpen: isOpen, closeSearch } = useCommandSearchContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { openQuickAdd } = useQuickAdd();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();


  // Define all searchable pages with their metadata
  const getAllPages = (): SearchResult[] => {
    const pages: SearchResult[] = [
      // Main pages
      {
        id: 'dashboard',
        title: t('navigation.dashboard'),
        description: t('dashboard.description'),
        path: '/',
        category: t('common.main'),
        keywords: ['dashboard', 'inicio', 'home', 'panel', 'resumen', 'summary'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'calendar',
        title: t('navigation.calendar'),
        description: t('calendar.calendarDescription'),
        path: '/calendar',
        category: t('common.main'),
        keywords: ['calendar', 'calendario', 'schedule', 'agenda', 'fechas', 'dates', 'eventos', 'events'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Services
      {
        id: 'services',
        title: t('navigation.services'),
        description: t('services.title'),
        path: '/servicios',
        category: t('navigation.services'),
        keywords: ['services', 'servicios', 'tours', 'visitas', 'guiadas', 'guided'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'new-service',
        title: t('services.newService'),
        description: t('services.createNewService'),
        path: '/servicios',
        action: () => openQuickAdd(),
        category: t('navigation.services'),
        keywords: ['new service', 'nuevo servicio', 'crear', 'create', 'añadir', 'add', 'tour'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 4v16m8-8H4"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Invoicing
      {
        id: 'invoices',
        title: "Finanzas",
        description: t('invoicing.title'),
        path: '/facturacion',
        category: "Finanzas",
        keywords: ['invoices', 'facturas', 'billing', 'facturación', 'cobros', 'payments'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 14l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'invoice-templates',
        title: t('invoicing.templates'),
        description: 'Gestiona las plantillas de facturas',
        path: '/facturacion/templates',
        category: "Finanzas",
        keywords: ['templates', 'plantillas', 'invoice templates', 'diseño', 'design', 'personalizar'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Clients
      {
        id: 'clients',
        title: t('navigation.clients'),
        description: t('clients.management'),
        path: '/clientes',
        category: t('navigation.clients'),
        keywords: ['clients', 'clientes', 'customers', 'agencias', 'agencies', 'contactos'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Files
      {
        id: 'files',
        title: t('navigation.files'),
        description: t('files.title'),
        path: '/archivos',
        category: t('common.main'),
        keywords: ['files', 'archivos', 'documents', 'documentos', 'upload', 'subir', 'descargar'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Analytics
      {
        id: 'analytics',
        title: 'Analíticas',
        description: 'Estadísticas y métricas de negocio',
        path: '/analiticas',
        category: t('navigation.tools'),
        keywords: ['analytics', 'analíticas', 'estadísticas', 'stats', 'métricas', 'metrics', 'reportes'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>,
        requiredPermission: 'guia'
      },
      
      // Configuration
      {
        id: 'config',
        title: t('navigation.dataConfiguration'),
        description: 'Configuración de datos maestros',
        path: '/configuracion',
        category: t('navigation.tools'),
        keywords: ['configuration', 'configuración', 'settings', 'ajustes', 'datos', 'data'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
        requiredPermission: 'guia'
      },
      
      // User Account
      {
        id: 'profile',
        title: t('navigation.myProfile'),
        description: t('profile.description'),
        path: '/profile',
        category: t('common.account'),
        keywords: ['profile', 'perfil', 'account', 'cuenta', 'personal', 'información'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>,
        requiredPermission: 'guia'
      },
      {
        id: 'settings',
        title: t('dashboard.settings'),
        description: t('settings.description'),
        path: '/settings',
        category: t('common.account'),
        keywords: ['settings', 'ajustes', 'preferences', 'preferencias', 'configuración', 'configuration'],
        icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>,
        requiredPermission: 'guia'
      }
    ];

    // Filter pages based on user permissions
    return pages.filter(page => {
      if (!page.requiredPermission) return true;
      if (!user) return false;
      
      if (user.permisos === 'superadmin') return true;
      if (page.requiredPermission === 'admin' && user.permisos === 'admin') return true;
      if (page.requiredPermission === 'guia' && (user.permisos === 'guia' || user.permisos === 'admin')) return true;
      
      return false;
    });
  };

  // Filter results based on search query
  const filteredResults = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    const allPages = getAllPages();
    
    return allPages.filter(page => {
      // Search in title, description and keywords
      const inTitle = page.title.toLowerCase().includes(query);
      const inDescription = page.description?.toLowerCase().includes(query) || false;
      const inKeywords = page.keywords.some(keyword => keyword.toLowerCase().includes(query));
      
      return inTitle || inDescription || inKeywords;
    }).sort((a, b) => {
      // Prioritize exact matches in title
      const aExact = a.title.toLowerCase() === query;
      const bExact = b.title.toLowerCase() === query;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      // Then prioritize title matches
      const aTitle = a.title.toLowerCase().includes(query);
      const bTitle = b.title.toLowerCase().includes(query);
      if (aTitle && !bTitle) return -1;
      if (!aTitle && bTitle) return 1;
      
      return 0;
    });
  }, [searchQuery, i18n.language, t, user]);

  // Group results by category
  const groupedResults = React.useMemo(() => {
    const groups: Record<string, SearchResult[]> = {};
    
    filteredResults.forEach(result => {
      if (!groups[result.category]) {
        groups[result.category] = [];
      }
      groups[result.category].push(result);
    });
    
    return groups;
  }, [filteredResults]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else if (!isOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % filteredResults.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + filteredResults.length) % filteredResults.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredResults[selectedIndex]) {
            runResult(filteredResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          closeSearch();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredResults, navigate, closeSearch]);

  const runResult = (result: SearchResult) => {
    closeSearch();
    if (result.action) return result.action();
    navigate(result.path);
  };

  
  // Si no está abierto, no renderizar nada
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-[99999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeSearch();
        }
      }}
    >
      <div className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl shadow-2xl mt-16 overflow-hidden border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center flex-1 space-x-3">
            <svg
              className="w-5 h-5 text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar páginas y funciones..."
              className="flex-1 text-lg bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
          <button
            onClick={closeSearch}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto">
          {!searchQuery.trim() ? (
            <div className="p-6 text-center">
              <div className="text-gray-500 dark:text-gray-400 mb-4">
                <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-sm">Empieza a escribir para buscar...</p>
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 space-y-1">
                <p>• "servicios" para gestionar visitas guiadas</p>
                <p>• "facturas" para facturación</p>
                <p>• "clientes" para contactos</p>
              </div>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              <svg className="w-8 h-8 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm">No se encontraron resultados para "{searchQuery}"</p>
            </div>
          ) : (
            <div>
              {Object.entries(groupedResults).map(([category, results]) => (
                <div key={category}>
                  <div className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                    {category}
                  </div>
                  {results.map((result, index) => {
                    const globalIndex = filteredResults.indexOf(result);
                    return (
                      <button
                        key={result.id}
                        onClick={() => runResult(result)}
                        onMouseEnter={() => setSelectedIndex(globalIndex)}
                        className={`w-full flex items-center space-x-3 p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                          selectedIndex === globalIndex ? 'bg-gray-50 dark:bg-gray-800/50' : ''
                        }`}
                      >
                        <div className="flex-shrink-0 text-gray-400 dark:text-gray-500">
                          {result.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {result.title}
                          </div>
                          {result.description && (
                            <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {result.description}
                            </div>
                          )}
                        </div>
                        {selectedIndex === globalIndex && (
                          <div className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
                            ↵
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-4">
              <span className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs">↑↓</kbd>
                <span>navegar</span>
              </span>
              <span className="flex items-center space-x-1">
                <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs">↵</kbd>
                <span>abrir</span>
              </span>
            </div>
            <span className="flex items-center space-x-1">
              <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-xs">esc</kbd>
              <span>cerrar</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileCommandSearch;